import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { prParamsToUrl } from '../../utilities/urlResolver';
import { buildSearch, getWizardV2State } from './wizardService';
import { selectFilterData } from '../mainFilter/selectors';
import { updateSecondaryFilterFromUrl } from '../secondaryFilter/secondaryFilterSlice';
import { updateDatesFilter, updateMainFilterFromUrl } from '../mainFilter/mainFilterSlice';
import { updateSearch } from '../mainFilter/searchSlice';
import { setWidgetStartSearch } from '../wizard-v2/onboardingSlice';
import { setAppState } from '../../appSlice';
import useUserPreferences from '../../hooks/preferences/useUserPreferences';

const useStartWizardSearch = () => {
  const dispatch = useDispatch()
  const { formV2: form } = useSelector(state => state.onboarding);
  const { searchEngine } = useSelector(selectFilterData);
  const similarDestinationsStep = useMemo(() => getWizardV2State().similarDestinationsStep, []);
  const { user, entryFlowParams } = useSelector(state => state.user);
  const { preferences } = useUserPreferences(user?.muid || '');
  const navigate = useNavigate();

  const startSearch = useCallback(() => {
    const searchState = buildSearch(form, entryFlowParams);
    const prSearchState = prParamsToUrl(false);
    const showSimilarDestStep = !!((entryFlowParams.entryFlow === 'link-presets' || entryFlowParams.entryFlow === 'link-presets-welcome') && entryFlowParams.similarDestinations && entryFlowParams.destinations && similarDestinationsStep);
    const data = {
      nights: searchState.nights,
      months: searchState.months.map(i => +i),
      type: searchState.type,
      weekendsOnly: searchState.weekendsOnly,
      startDate: searchState.startDate?.replace(/-/g, '/'),
      endDate: searchState.endDate?.replace(/-/g, '/'),
    }
    let combinedData = {
      ...data
    };
    if (prSearchState) {
      combinedData = {
        ...prSearchState,
        ...data
      };
    }

    if (preferences.breakfast_quality) {
      searchState.board = 'bb';
    }

    // functionality for the similarDestinationsStep and entryFlow
    if (showSimilarDestStep) {
      const mainDestinationId = (typeof searchState.destinations === 'object' && searchState.destinations.length) ? searchState.destinations[0] : searchState.destinations;
      if (form.similarDestinations === 'onlySpecific' || !form.similarDestinations) {
        // only selected destination: remove what and group, leave only destination
        delete searchState.what;
        delete combinedData.what;
        delete searchState.destinationsGroups;
        delete combinedData.destinationsGroups;
      } else if (form.similarDestinations === 'similarDestinations') {
        // destination + similar:
        // 1. get destinationsGroups from url or 2. get what theme from url or 3. get destinationsGroup from destinations_group_main
        const dest = searchEngine.where.destinations.filter((destination) => destination.id === mainDestinationId);
        let destGroups = '';
        if (searchState.destinationsGroups) {
          destGroups = searchState.destinationsGroups;
          delete searchState.what;
          delete combinedData.what;
        } else if (!searchState.destinationsGroups && searchState.what) {
          delete searchState.destinations;
          delete combinedData.destinations;
        } else if (!searchState.destinationsGroups && !searchState.what && dest.length && dest[0].destinations_group_main) {
          destGroups = dest[0].destinations_group_main;
        }
        let allDestId = mainDestinationId;
        if (destGroups) {
          allDestId = searchEngine.where.destinations.filter((destination) => (destination.destinations_group_main === destGroups || destination.destinations_groups.indexOf(destGroups) > -1)).map(dest => dest.id);
          if (!searchState.destinationsGroups) {
            searchState.destinationsGroups = destGroups;
            combinedData.destinationsGroups = destGroups;
          }
        }
        if (allDestId && typeof allDestId === 'object' && allDestId.length > 1) {
          searchState.destinations = allDestId;
          combinedData.destinations = allDestId;
        }
        if (mainDestinationId) {
          searchState.mainDestinationId = mainDestinationId;
        }
      } else if (form.similarDestinations === 'everywhere') {
        // everywhere (without selected destination): remove destination, group and what
        delete searchState.what;
        delete combinedData.what;
        delete searchState.destinationsGroups;
        delete combinedData.destinationsGroups;
        delete searchState.destinations;
        delete combinedData.destinations;
      }
    }

    const urlGetParams = new URLSearchParams(window.location.search);
    const movsessid = urlGetParams.get('movsessid');


    if (window.location.search.indexOf('wizardWidget=true') > -1) {
      const widgetStartSearch = {
        wizardWidgetStartSearch:true,
        searchUrl: queryString.stringify(searchState, {
          arrayFormat: 'comma',
        }),
        movsessid: movsessid,
      };
      dispatch(setWidgetStartSearch(widgetStartSearch));
    } else {
      // Create a promise to ensure sequential execution
      Promise.resolve()
        .then(() => {
          const hash = queryString.stringify(searchState, {
            arrayFormat: 'comma',
          });
          navigate({
            hash,
            search: window.location.search.substring(1)
          });
        })
        .then(() => {
          // Only run these after history has been updated
          dispatch(updateSecondaryFilterFromUrl());
          dispatch(updateMainFilterFromUrl());
          dispatch(updateSearch({ when: combinedData }));
          dispatch(updateDatesFilter(combinedData));
          dispatch(setAppState({ wizardCompleted: true }));
        });
    }
  }, [dispatch, form, similarDestinationsStep, entryFlowParams, navigate, preferences]);

  return {
    startSearch
  }
}

export default useStartWizardSearch;
